import { useState } from 'react';
import { PROVIDERS_DRAWER_ACTION_VALUES } from '../components/drawers';
import useTranslate from '../../../../../hooks/useTranslate';
import { useTranslation } from 'react-i18next';
import useTableMenu from '../../../../../hooks/useTableMenu';
import useGlobalFilters from '../../../../../hooks/useGlobalFilters';
import { useDeleteCarcinogenicProviderMutation } from '../../../../../services/carcinogenicApi';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import useAlerts from './useAlerts';
import { enqueueSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { updateProvidersEmptyDeclarationCheck } from '../../../../../redux/slices/carcinogenicReportSlice';

const useProvidersModule = () => {
    const dispatch = useDispatch();
    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );

    const providersEmptyDeclarationCheckState = useAppSelector(
        (state) => state?.carcinogenicReport?.providersEmptyDeclarationCheck
    );

    const toggleProvidersEmptyDeclarationCheck = ({ customValue }: { customValue?: boolean }) => {
        dispatch(
            updateProvidersEmptyDeclarationCheck(
                customValue !== undefined ? customValue : !providersEmptyDeclarationCheckState
            )
        );
    };

    const [selected, setSelected] = useState<any | null>(null);
    const [open, setOpen] = useState<string>('');
    const { t } = useTranslation('carcinogenicReport', { keyPrefix: 'modules.providersAndBuyers' });
    const { translate } = useTranslate(t);

    const { showConfirmDeleteAlert } = useAlerts({ translate });

    const [deleteProvider, { isLoading: isDeletingProvider }] =
        useDeleteCarcinogenicProviderMutation();

    const providersTableMenu = useTableMenu();
    const providersFilters = useGlobalFilters();

    const { anchorToEventElement, openMenu } = providersTableMenu;

    const handleClickNewProviderButton = () => {
        setOpen(PROVIDERS_DRAWER_ACTION_VALUES.NEW_PROVIDER);
    };

    const handleClickEditProviderButton = (selectedSubstance: any) => {
        setSelected(selectedSubstance);
        setOpen(PROVIDERS_DRAWER_ACTION_VALUES.EDIT_PROVIDER);
    };

    const handleClickDeleteProviderButton = (selectedSubstance: any) => {
        showConfirmDeleteAlert({
            confirmAction: () => handleDeleteProvider({ selectedSubstance })
        });
    };

    const handleClickAction = (event: any, item: any) => {
        setSelected(item);
        anchorToEventElement(event);
        openMenu();
    };

    const handleDeleteProvider = async ({ selectedSubstance }: any) => {
        try {
            if (
                !selectedCarcinogenicReportId ||
                !selectedSubstance?.id ||
                !selectedSubstance?.proveedor?.id
            )
                return;

            const response: any = await deleteProvider({
                reportId: selectedCarcinogenicReportId,
                substanceId: selectedSubstance?.id,
                providerId: selectedSubstance?.proveedor?.id
            });
            if (response?.error) {
                enqueueSnackbar(translate('enqueues.delete.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(translate('enqueues.delete.success'), {
                    variant: 'success'
                });
            }
        } catch (error) {
            enqueueSnackbar(translate('enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    };

    return {
        selected,
        open,
        providersTableMenu,
        providersFilters,
        isDeletingProvider,
        providersEmptyDeclarationCheckState,
        toggleProvidersEmptyDeclarationCheck,
        translate,
        setOpen,
        handleClickNewProviderButton,
        handleClickEditProviderButton,
        handleClickDeleteProviderButton,
        handleClickAction
    };
};

export default useProvidersModule;
